import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { DeviceUserService } from '../../services/device-user.service';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { AuthService } from '../../services/auth.service';
import { UnmappedDevicePunchService } from '../../services/unmapped-device-punch.service';
import { DeviceUser } from '../../interfaces/deviceUser.interface';
import { UnmappedPunchCount } from '../../interfaces/unmappedDevicePunch.interface';
import {
    Modal,
    Button,
    Box,
    Pagination,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Tab,
    Tabs,
    CircularProgress,
    InputAdornment,
    Badge,
} from '@mui/material';
import { LinkOutlined, LinkOffOutlined } from '@mui/icons-material';
import { displayUsername } from '../../utils/userUtils';
import { EmployeeAutocomplete, employeeLabelWithFullName } from '../common/EmployeeAutocomplete';
import ScrollableTable from '../common/ScrollableTable';
import UnmappedPunchList from './UnmappedPunchList';

interface SimpleUser {
    id: string;
    username: string;
    fullName?: string;
}

const DeviceUserList = () => {
    const [deviceUsers, setDeviceUsers] = useState<DeviceUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'all' | 'unmapped' | 'punches'>('all');
    const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });
    const [punchCount, setPunchCount] = useState<UnmappedPunchCount>({ heldGroups: 0, heldPunches: 0 });

    // Mapping modal state
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [selectedDeviceUser, setSelectedDeviceUser] = useState<DeviceUser | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<SimpleUser | null>(null);
    const [mapping, setMapping] = useState(false);
    const [unmappingId, setUnmappingId] = useState<string | null>(null);

    // Institution code for username prefix
    const [institutionCode, setInstitutionCode] = useState<string>('');

    // Create & Map tab state
    const [modalTab, setModalTab] = useState(0);
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newFirstName, setNewFirstName] = useState('');
    const [newLastName, setNewLastName] = useState('');
    const [creating, setCreating] = useState(false);

    // Table search state
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const toast = useToast();

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        const institutionId = AuthService.getSelectedInstitution();
        // The code is cached at login/institution selection; only non-admins lack access to the
        // institution record, so prefer the cache and never toast when the lookup is refused.
        const cachedCode = AuthService.getSelectedInstitutionCode();
        if (cachedCode) {
            setInstitutionCode(cachedCode);
        } else if (institutionId) {
            InstitutionService.getInstitutionById(institutionId)
                .then((data: { code: string }) => setInstitutionCode(data.code))
                .catch(() => { /* prefix is cosmetic; leave it blank */ });
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        fetchPunchCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (viewMode === 'punches') return;
        fetchDeviceUsers(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, debouncedSearch]);

    const fetchPunchCount = async () => {
        try {
            const response = await UnmappedDevicePunchService.getCount();
            setPunchCount(response);
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load unmapped punch count');
        }
    };

    const fetchDeviceUsers = async (page: number = 0) => {
        try {
            setLoading(true);
            const response = viewMode === 'all'
                ? await DeviceUserService.getByInstitution(page, 10, debouncedSearch)
                : await DeviceUserService.getUnmapped(page, 10, debouncedSearch);
            setDeviceUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, 'Failed to load device users');
        } finally {
            setLoading(false);
        }
    };

    const resetCreateForm = () => {
        setNewUsername('');
        setNewPassword('');
        setNewPhone('');
        setNewFirstName('');
        setNewLastName('');
    };

    // The server requires a first and last name on every new login; start them from the machine name.
    const prefillFromDevice = (deviceUser: DeviceUser) => {
        const parts = (deviceUser.username || '').trim().split(/\s+/).filter(Boolean);
        setNewUsername((deviceUser.username || '').replace(/\s+/g, '_'));
        setNewFirstName(parts[0] ?? '');
        setNewLastName(parts.slice(1).join(' '));
    };

    const createMissing = !newUsername || !newPassword || !newFirstName.trim() || !newLastName.trim();

    const handleOpenMapModal = (deviceUser: DeviceUser) => {
        setSelectedDeviceUser(deviceUser);
        setSelectedUserId(null);
        setModalTab(0);
        resetCreateForm();
        prefillFromDevice(deviceUser);
        setIsMapModalOpen(true);
    };

    const handleCloseMapModal = () => {
        setIsMapModalOpen(false);
        setSelectedDeviceUser(null);
        setSelectedUserId(null);
        setModalTab(0);
        resetCreateForm();
    };

    const handleMapUser = async () => {
        if (!selectedDeviceUser || !selectedUserId) return;

        try {
            setMapping(true);
            await DeviceUserService.mapToUser(selectedDeviceUser.id, selectedUserId.id);
            toast.showSuccess('Device user mapped successfully');
            handleCloseMapModal();
            await fetchDeviceUsers(pageInfo.currentPage);
        } catch (err: any) {
            toast.showApiError(err, 'Failed to map device user');
        } finally {
            setMapping(false);
        }
    };

    const handleCreateAndMap = async () => {
        if (!selectedDeviceUser || createMissing) return;

        try {
            setCreating(true);
            const newUser = await UserService.createUser({
                username: institutionCode ? `${institutionCode}_${newUsername}` : newUsername,
                password: newPassword,
                phone: newPhone,
                firstName: newFirstName.trim(),
                lastName: newLastName.trim(),
            });
            await DeviceUserService.mapToUser(selectedDeviceUser.id, newUser.id);
            toast.showSuccess('User created and mapped successfully');
            handleCloseMapModal();
            await fetchDeviceUsers(pageInfo.currentPage);
        } catch (err: any) {
            toast.showApiError(err, 'Failed to create and map user');
        } finally {
            setCreating(false);
        }
    };

    const handleUnmapUser = async (deviceUser: DeviceUser) => {
        const mappedLabel = deviceUser.mappedUsername ? displayUsername(deviceUser.mappedUsername) : 'the mapped user';
        if (!window.confirm(
            `Unmap "${deviceUser.username || deviceUser.deviceUserId}" from ${mappedLabel}? ` +
            `New punches from this device user will stop being recorded until it is mapped again.`
        )) {
            return;
        }
        try {
            setUnmappingId(deviceUser.id);
            await DeviceUserService.unmapUser(deviceUser.id);
            toast.showSuccess('Device user unmapped successfully');
            await fetchDeviceUsers(pageInfo.currentPage);
        } catch (err: any) {
            toast.showApiError(err, 'Failed to unmap device user');
        } finally {
            setUnmappingId(null);
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchDeviceUsers(page - 1);
    };

    const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'all' | 'unmapped' | 'punches' | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return format(parseISO(dateStr), 'dd-MM-yyyy HH:mm');
    };

    if (loading && deviceUsers.length === 0) return <div>Loading...</div>;

    return (
        <div style={{ padding: '20px' }} data-help-page="deviceUser.list.page">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Device Users</h2>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                    <TextField
                        data-help="deviceUser.list.search"
                        label="Search by username"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flex: '1 1 220px', maxWidth: 360 }}
                    />
                    <ToggleButtonGroup
                        data-help="deviceUser.list.viewMode"
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="unmapped">Unmapped</ToggleButton>
                        <ToggleButton value="punches">
                            <Badge badgeContent={punchCount.heldPunches} color="error" sx={{ pr: 1 }}>
                                Unmapped punches
                            </Badge>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </div>

            {viewMode === 'punches' ? (
                <UnmappedPunchList onChanged={fetchPunchCount} />
            ) : (
                <>
                    <div style={{ marginBottom: '20px' }}>
                        Total: {pageInfo.totalElements}
                    </div>
                    <ScrollableTable>
                    <table data-help-section="deviceUser.list.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                        <thead>
                            <tr>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.deviceId">Device ID</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.deviceUserId">Device User ID</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.username">Username</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.pin">User PIN</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.department">Department</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.fingerprints">Fingerprints</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.enrolledAt">Enrolled At</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.mapped">Mapped User</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.lastSynced">Last Synced</th>
                                <th style={tableHeaderStyle} data-help="deviceUser.list.col.actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deviceUsers.map(du => (
                                <tr key={du.id}>
                                    <td style={tableCellStyle}>{du.deviceId}</td>
                                    <td style={tableCellStyle}>{du.deviceUserId}</td>
                                    <td style={tableCellStyle}>{du.username || '-'}</td>
                                    <td style={tableCellStyle}>{du.userPin || '-'}</td>
                                    <td style={tableCellStyle}>{du.departmentName || '-'}</td>
                                    <td style={tableCellStyle}>{du.fingerprintCount}</td>
                                    <td style={tableCellStyle}>{formatDate(du.enrolledAt)}</td>
                                    <td style={tableCellStyle}>
                                        {du.mappedUserId ? (
                                            <Chip
                                                label={du.mappedUsername ? displayUsername(du.mappedUsername) : 'Mapped'}
                                                color="success"
                                                size="small"
                                            />
                                        ) : (
                                            <Chip label="Unmapped" color="warning" size="small" variant="outlined" />
                                        )}
                                    </td>
                                    <td style={tableCellStyle}>{formatDate(du.lastSyncedAt)}</td>
                                    <td style={tableCellStyle}>
                                        {!du.mappedUserId ? (
                                            <Button
                                                data-help="deviceUser.list.action.map"
                                                size="small"
                                                variant="outlined"
                                                startIcon={<LinkOutlined />}
                                                onClick={() => handleOpenMapModal(du)}
                                            >
                                                Map User
                                            </Button>
                                        ) : (
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    data-help="deviceUser.list.action.remap"
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<LinkOutlined />}
                                                    onClick={() => handleOpenMapModal(du)}
                                                >
                                                    Remap
                                                </Button>
                                                <Button
                                                    data-help="deviceUser.list.action.unmap"
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    startIcon={<LinkOffOutlined />}
                                                    disabled={unmappingId === du.id}
                                                    onClick={() => handleUnmapUser(du)}
                                                >
                                                    {unmappingId === du.id ? 'Unmapping...' : 'Unmap'}
                                                </Button>
                                            </Box>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {deviceUsers.length === 0 && (
                                <tr>
                                    <td style={tableCellStyle} colSpan={10}>
                                        No device users found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    </ScrollableTable>

                    {pageInfo.totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={pageInfo.totalPages}
                                page={pageInfo.currentPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Map User Modal */}
            <Modal open={isMapModalOpen} onClose={handleCloseMapModal}>
                <Box data-help-page="deviceUser.map.page" sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 450,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>Map Device User to OMS User</h3>
                    {selectedDeviceUser && (
                        <Box sx={{ mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                            Device: {selectedDeviceUser.deviceId} | User ID: {selectedDeviceUser.deviceUserId}
                            {selectedDeviceUser.username && ` | Username: ${selectedDeviceUser.username}`}
                            {selectedDeviceUser.userPin && ` | PIN: ${selectedDeviceUser.userPin}`}
                        </Box>
                    )}
                    <Tabs data-help="deviceUser.list.mapTabs" value={modalTab} onChange={(_e, v) => { setModalTab(v); resetCreateForm(); if (v === 1 && selectedDeviceUser) prefillFromDevice(selectedDeviceUser); setSelectedUserId(null); }} sx={{ mb: 2 }}>
                        <Tab label="Select Existing" />
                        <Tab label="Create & Map" />
                    </Tabs>

                    {modalTab === 0 && (
                        <EmployeeAutocomplete<SimpleUser>
                            data-help="deviceUser.map.field.employee" label="Select OMS User"
                            placeholder="Search users..."
                            size="medium"
                            fullWidth
                            getOptionLabel={employeeLabelWithFullName}
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            onError={() => toast.showError('Failed to search users')}
                        />
                    )}

                    {modalTab === 1 && (
                        <Box data-help-section="deviceUser.map.section.create" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                data-help="deviceUser.map.field.firstName" label="First name"
                                value={newFirstName}
                                onChange={(e) => setNewFirstName(e.target.value)}
                                fullWidth
                                required
                                inputProps={{ maxLength: 60 }}
                            />
                            <TextField
                                data-help="deviceUser.map.field.lastName" label="Last name"
                                value={newLastName}
                                onChange={(e) => setNewLastName(e.target.value)}
                                fullWidth
                                required
                                inputProps={{ maxLength: 60 }}
                            />
                            <TextField
                                data-help="deviceUser.map.field.username" label="Username"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value.replace(/\s/g, '_'))}
                                fullWidth
                                required
                                InputProps={institutionCode ? {
                                    startAdornment: (
                                        <InputAdornment position="start">{institutionCode}_</InputAdornment>
                                    )
                                } : {}}
                            />
                            <TextField
                                data-help="deviceUser.map.field.password" label="Password"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                data-help="deviceUser.map.field.phone" label="Phone"
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                fullWidth
                            />
                        </Box>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button data-help="deviceUser.map.cancel" onClick={handleCloseMapModal} variant="outlined">
                            Cancel
                        </Button>
                        {modalTab === 0 ? (
                            <Button
                                data-help="deviceUser.map.save" onClick={handleMapUser}
                                variant="contained"
                                color="primary"
                                disabled={!selectedUserId || mapping}
                                {...helpWhy(mapping ? { kind: 'status', message: 'The mapping is being saved.' } : !selectedUserId ? { kind: 'status', message: 'Pick the employee first.' } : null)}
                            >
                                {mapping ? 'Mapping...' : 'Map User'}
                            </Button>
                        ) : (
                            <Button
                                data-help="deviceUser.map.createSave" onClick={handleCreateAndMap}
                                variant="contained"
                                color="primary"
                                disabled={createMissing || creating}
                                {...helpWhy(creating ? { kind: 'status', message: 'The login is being created.' } : createMissing ? { kind: 'status', message: 'Fill in the first name, last name, username and password first.' } : null)}
                                startIcon={creating ? <CircularProgress size={16} /> : undefined}
                            >
                                {creating ? 'Creating...' : 'Create & Map'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default DeviceUserList;
